// Fuse Rating Calculator: field metadata + client-side per-field validation, used for
// instant inline feedback as the user types. The actual sizing math (wire ampacity,
// voltage drop, fuse selection with ambient derating, breaking-capacity and
// coordination checks) and the authoritative (server-side) validation live in the main
// backend -- see src/lib/toolsApi.js (fuseRatingToolApi) and
// backend/tools/fuse_rating_tool.py. Reference standards (SAE J1128/J1742, ISO
// 6722/8820, UL 248, IEC 60269, ISO 6469-3) are cited for context/tooltips only -- see
// backend/core/lookup/fuse_rating_specs.py header for the tool's sourcing posture.

export const STANDARD_SIZES_MM2 = [0.35, 0.5, 0.75, 1.0, 1.5, 2.5, 4, 6, 10, 16, 25, 35, 50, 70, 95, 120, 150, 185, 240];

export const AWG_LABEL_BY_MM2 = {
  0.35: '22 AWG', 0.5: '20 AWG', 0.75: '18 AWG', 1.5: '16 AWG', 2.5: '14 AWG',
  4: '12 AWG', 6: '10 AWG', 10: '8 AWG', 16: '6 AWG', 25: '3 AWG', 35: '2 AWG',
  50: '1/0 AWG', 70: '2/0 AWG', 95: '3/0 AWG', 120: '250 kcmil', 150: '300 kcmil',
  185: '350 kcmil', 240: '500 kcmil',
};

export const VOLTAGE_PRESETS_LV = [12, 24, 48];

export const CONDUCTOR_MATERIALS = [
  { value: 'copper', label: 'Copper' },
  { value: 'aluminum', label: 'Aluminum' },
];

export const INSULATION_RATINGS = [
  { value: 'txl', label: 'TXL (125°C, thin-wall)' },
  { value: 'gxl', label: 'GXL (125°C, thick-wall)' },
  { value: 'xlpe', label: 'XLPE, automotive/EV (125°C)' },
  { value: 'custom', label: 'Custom Temp Rating…' },
];

export const LV_FUSE_FAMILIES = [
  { value: 'mini_ato', label: 'Mini / ATO Blade' },
  { value: 'maxi', label: 'Maxi Blade' },
  { value: 'micro2', label: 'Micro2 Blade' },
  { value: 'glass_tube', label: 'Glass Tube (AGC/3AG)' },
  { value: 'bolt_down', label: 'Bolt-Down (MEGA/AMG)' },
];

export const EV_HV_FUSE_FAMILIES = [
  { value: 'hv_blade_cartridge', label: 'HV Blade / Cartridge Fuse' },
  { value: 'pyro_fuse', label: 'Pyro-Fuse (Pyrotechnic Disconnect)' },
];

export const DEFAULT_INPUTS = {
  mode: 'lv',
  systemVoltageV: 12,
  inputMode: 'current',
  loadCurrentA: 20,
  loadPowerW: 240,
  wireLengthM: 3,
  conductorMaterial: 'copper',
  insulationRating: 'gxl',
  customInsulationMaxTempC: 105,
  bundleSize: 1,
  ambientTempWireC: 25,
  ambientTempFuseC: 25,
  maxVoltageDropPercent: 3,
  availableFaultCurrentA: 500,
  marginFactorLv: 1.25,
  marginFactorEvHv: 1.5,
  lvFuseFamily: 'mini_ato',
  evHvFuseFamily: 'hv_blade_cartridge',
  breakingCapacityKa: 20,
};

// min / max mirror NUMERIC_FIELD_SPECS in backend/core/lookup/fuse_rating_specs.py.
export const NUMERIC_FIELD_SPECS = [
  { key: 'systemVoltageV', label: 'System Voltage', unit: 'V', min: 1, max: 1000, strictlyPositive: true },
  { key: 'loadCurrentA', label: 'Continuous Load Current', unit: 'A', min: 0, max: 2000, strictlyPositive: true },
  { key: 'loadPowerW', label: 'Continuous Load Power', unit: 'W', min: 0, max: 500000, strictlyPositive: true },
  { key: 'wireLengthM', label: 'Wire Length, One-Way', unit: 'm', min: 0, max: 500, strictlyPositive: true },
  { key: 'customInsulationMaxTempC', label: 'Custom Insulation Max Temp', unit: '°C', min: 60, max: 200, strictlyPositive: true },
  { key: 'bundleSize', label: 'Bundle Size', unit: 'conductors', min: 1, max: 60, strictlyPositive: true },
  { key: 'ambientTempWireC', label: 'Ambient Temperature at Wire', unit: '°C', min: -40, max: 125, strictlyPositive: false },
  { key: 'ambientTempFuseC', label: 'Ambient Temperature at Fuse', unit: '°C', min: -40, max: 125, strictlyPositive: false },
  { key: 'maxVoltageDropPercent', label: 'Max Allowable Voltage Drop', unit: '%', min: 0.1, max: 15, strictlyPositive: true },
  { key: 'availableFaultCurrentA', label: 'Available Fault Current', unit: 'A', min: 0, max: 200000, strictlyPositive: true },
  { key: 'marginFactor', label: 'Safety / Derating Margin Factor', unit: '×', min: 1.0, max: 3.0, strictlyPositive: true },
  { key: 'breakingCapacityKa', label: 'Required Interrupt / Breaking Capacity', unit: 'kA', min: 0.1, max: 200, strictlyPositive: true },
];

export function validateField(spec, rawValue) {
  if (rawValue === '' || rawValue === null || rawValue === undefined) {
    return 'Required.';
  }
  const value = Number(rawValue);
  if (Number.isNaN(value)) {
    return 'Must be a number.';
  }
  if (spec.strictlyPositive && value <= 0) {
    return 'Must be greater than 0.';
  }
  if (spec.min !== null && value < spec.min) {
    return `Must be ≥ ${spec.min}.`;
  }
  if (spec.max !== null && value > spec.max) {
    return `Must be ≤ ${spec.max}.`;
  }
  return null;
}

export const CELSIUS_TO_FAHRENHEIT = (c) => (c * 9) / 5 + 32;
export const FAHRENHEIT_TO_CELSIUS = (f) => ((f - 32) * 5) / 9;
export const M_TO_FT = 3.28084;
